import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  getComplaintSummary,
  listComplaints,
  runComplaintCheck,
  type ComplaintHit,
  type ComplaintSummary,
} from "../api/complaints";

const PAGE_SIZE = 100;

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function n(v: number | null | undefined) {
  return (v ?? 0).toLocaleString();
}
function d(iso: string | null | undefined) {
  return iso ? iso.slice(0, 10) : "—";
}
function readApiError(e: unknown): string {
  const ax = e as { response?: { data?: { detail?: string; error?: string } }; message?: string };
  return ax.response?.data?.detail || ax.response?.data?.error || ax.message || "Something went wrong";
}

const SOURCE_LABEL: Record<string, string> = { fcc: "FCC", ftc: "FTC", report: "Report" };

function StatusBadge({ status }: { status: ComplaintHit["did_status"] }) {
  const cls =
    status === "active" ? "bg-red-100 text-red-800"          // active WITH a complaint = the problem
    : status === "quarantined" ? "bg-amber-100 text-amber-800"
    : status === "deleted" ? "bg-gray-200 text-gray-600"
    : "bg-gray-100 text-gray-500";
  const label = status === "deleted" ? "released" : status;
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{label}</span>;
}

// The ownership evidence, spelled out per row: how long after we acquired the
// number the complained-about call happened, and whether we'd started dialing.
function OwnershipBadge({ hit }: { hit: ComplaintHit }) {
  if (hit.during_ownership === null)
    return <span className="px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-800" title="This complaint came from a report with no complaint date, so whether it happened while we held the number is unverified">date unknown</span>;
  if (hit.action === "prior_owner")
    return <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500">prior owner</span>;
  if (hit.action === "after_release")
    return <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500">after release</span>;
  const days = hit.days_after_acquired;
  return (
    <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800" title="Complaint dated inside the window we held this number">
      ours{days != null ? ` · day ${days}` : ""}{hit.after_first_use === false ? " · before 1st use" : ""}
    </span>
  );
}

function ActionBadge({ action }: { action: string }) {
  const map: Record<string, string> = {
    quarantine: "bg-amber-100 text-amber-800",
    released: "bg-gray-200 text-gray-700",
    already_quarantined: "bg-amber-50 text-amber-700",
    recorded: "bg-blue-50 text-blue-700",
    prior_owner: "bg-gray-100 text-gray-500",
    after_release: "bg-gray-100 text-gray-500",
    reported_undated: "bg-amber-50 text-amber-800",
  };
  return <span className={`px-2 py-0.5 rounded text-xs ${map[action] ?? "bg-gray-100 text-gray-600"}`}>{action.replace("_", " ")}</span>;
}

function Tile({ label, value, tone = "default", hint }: { label: string; value: number; tone?: "default" | "danger" | "muted"; hint?: string }) {
  const valueCls = tone === "danger" && value > 0 ? "text-red-700" : tone === "muted" ? "text-gray-500" : "text-gray-800";
  return (
    <div className={`bg-white rounded-lg shadow p-3 ${tone === "danger" && value > 0 ? "ring-1 ring-red-200" : ""}`} title={hint}>
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-2xl font-bold tabular-nums ${valueCls}`}>{n(value)}</div>
    </div>
  );
}

// Single-series weekly bars (SVG): thin marks, rounded tops, hover tooltip,
// recessive baseline, one direct label on the peak. No legend — one series.
function WeeklyBars({ weeks }: { weeks: ComplaintSummary["weekly"] }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 560, H = 120, pad = 18, gap = 2;
  const max = Math.max(1, ...weeks.map((w) => w.hits));
  const bw = weeks.length ? Math.max(4, (W - pad * 2) / weeks.length - gap) : 0;
  const peak = weeks.reduce((m, w, i) => (w.hits > (weeks[m]?.hits ?? -1) ? i : m), 0);
  if (!weeks.length) return <p className="text-xs text-gray-400">No complaints in the last 12 weeks.</p>;
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-28 text-brand" role="img" aria-label="Complaints per week, last 12 weeks">
        <line x1={pad} x2={W - pad} y1={H - pad} y2={H - pad} stroke="currentColor" strokeOpacity={0.15} />
        {weeks.map((w, i) => {
          const h = Math.round(((H - pad * 2) * w.hits) / max);
          const x = pad + i * (bw + gap);
          const y = H - pad - h;
          return (
            <g key={w.week_start} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={x} y={pad} width={bw} height={H - pad * 2} fill="transparent" />
              <rect x={x} y={y} width={bw} height={h} rx={2} fill="currentColor" fillOpacity={hover === i ? 1 : 0.8} />
              {(i === peak || hover === i) && w.hits > 0 && (
                <text x={x + bw / 2} y={y - 4} textAnchor="middle" fontSize={10} className="fill-gray-700">{w.hits}</text>
              )}
              <title>{`Week of ${w.week_start}: ${w.hits} complaint${w.hits === 1 ? "" : "s"} on ${w.dids} number${w.dids === 1 ? "" : "s"}`}</title>
            </g>
          );
        })}
        <text x={pad} y={H - 4} fontSize={9} className="fill-gray-400">{weeks[0].week_start}</text>
        <text x={W - pad} y={H - 4} fontSize={9} textAnchor="end" className="fill-gray-400">{weeks[weeks.length - 1].week_start}</text>
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------

export function DidComplaintsPage() {
  const qc = useQueryClient();
  const [oursOnly, setOursOnly] = useState(true);
  const [days, setDays] = useState(0);
  const [source, setSource] = useState("");
  const [owner, setOwner] = useState<number | "">("");
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [runMsg, setRunMsg] = useState<string | null>(null);

  const scope = useMemo(() => ({
    ours_only: oursOnly, days: days || undefined, source: source || undefined,
    owner: owner === "" ? undefined : owner,
  }), [oursOnly, days, source, owner]);

  const summary = useQuery({ queryKey: ["did-complaints-summary", scope], queryFn: () => getComplaintSummary(scope) });
  const hits = useQuery({
    queryKey: ["did-complaints", scope, action, q, offset],
    queryFn: () => listComplaints({ ...scope, action: action || undefined, q: q || undefined, limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });
  const run = useMutation({
    mutationFn: runComplaintCheck,
    onSuccess: () => {
      setRunMsg("Sweep queued (FCC now; FTC walks the feed in the background). Results refresh as they land.");
      setTimeout(() => qc.invalidateQueries({ queryKey: ["did-complaints"] }), 20_000);
      setTimeout(() => qc.invalidateQueries({ queryKey: ["did-complaints-summary"] }), 20_000);
    },
    onError: (e) => setRunMsg(readApiError(e)),
  });

  const s = summary.data;
  const t = s?.totals;
  const total = hits.data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Caller-ID Complaints</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-3xl">
            Consumer complaints from public FCC and FTC records, and complaint reports, matched to our caller-ID numbers.
            A complaint counts as <strong>ours</strong> only when the call it describes is dated inside the window we held
            the number — on or after we acquired it and, if we've since released it, no later than that release.
            Earlier or later complaints belong to another owner, and complaint reports that carry no date cannot be
            verified either way — both are shown only when "All" is selected.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setRunMsg(null); run.mutate(); }}
            disabled={run.isPending}
            className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-2 text-sm disabled:opacity-50"
          >
            {run.isPending ? "Queuing…" : "Run sweep now"}
          </button>
        </div>
      </div>
      {runMsg && <p className="text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded px-3 py-2">{runMsg}</p>}

      {/* filters — one row */}
      <div className="bg-white rounded-lg shadow p-3 flex flex-wrap items-end gap-3 text-sm">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Ownership</label>
          <div className="inline-flex rounded border border-gray-300 overflow-hidden">
            {[{ v: true, l: "Ours (verified)" }, { v: false, l: "All (incl. undated & other owners)" }].map((o) => (
              <button key={String(o.v)} onClick={() => { setOursOnly(o.v); setOffset(0); }}
                className={`px-3 py-1.5 ${oursOnly === o.v ? "bg-brand text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label htmlFor="cw" className="block text-xs text-gray-500 mb-1">Window</label>
          <select id="cw" value={days} onChange={(e) => { setDays(Number(e.target.value)); setOffset(0); }} className="border border-gray-300 rounded px-2 py-1.5">
            <option value={0}>All time</option><option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option><option value={365}>Last 12 months</option>
          </select>
        </div>
        <div>
          <label htmlFor="cs" className="block text-xs text-gray-500 mb-1">Source</label>
          <select id="cs" value={source} onChange={(e) => { setSource(e.target.value); setOffset(0); }} className="border border-gray-300 rounded px-2 py-1.5">
            <option value="">All</option><option value="fcc">FCC</option><option value="ftc">FTC</option><option value="report">Report</option>
          </select>
        </div>
        <div>
          <label htmlFor="co" className="block text-xs text-gray-500 mb-1">Owner</label>
          <select id="co" value={owner} onChange={(e) => { setOwner(e.target.value === "" ? "" : Number(e.target.value)); setOffset(0); }} className="border border-gray-300 rounded px-2 py-1.5 min-w-[10rem]">
            <option value="">All owners</option>
            {(s?.by_owner ?? []).map((o) => (
              <option key={o.usr_id ?? "none"} value={o.usr_id ?? ""}>{o.username ?? `usr ${o.usr_id}`} ({n(o.hits)})</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="ca" className="block text-xs text-gray-500 mb-1">Action</label>
          <select id="ca" value={action} onChange={(e) => { setAction(e.target.value); setOffset(0); }} className="border border-gray-300 rounded px-2 py-1.5">
            <option value="">Any</option><option value="quarantine">quarantined</option><option value="released">released</option>
            <option value="already_quarantined">already quarantined</option><option value="recorded">recorded</option>
          </select>
        </div>
        <div>
          <label htmlFor="cq" className="block text-xs text-gray-500 mb-1">Number</label>
          <input id="cq" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} placeholder="digits…" className="border border-gray-300 rounded px-2 py-1.5 font-mono w-36" />
        </div>
      </div>

      {summary.isError && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{readApiError(summary.error)}</p>}

      {/* headline tiles */}
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-9 gap-3">
          <Tile label="Complaints (ours)" value={t.ours} hint="Dated inside the window we held the number" />
          <Tile label="Numbers affected" value={t.dids_ours} />
          <Tile label="Owners affected" value={t.owners_ours} />
          <Tile label="Still active" value={t.still_active_dids} tone="danger" hint="Numbers with a complaint on our watch that are STILL in rotation — act on these" />
          <Tile label="Quarantined by sweep" value={t.quarantined_by_sweep} />
          <Tile label="Released" value={t.released} />
          <Tile label="Last 7 days" value={t.ours_7d} />
          <Tile label="Reported, date unknown" value={t.undated} tone="muted" hint={'From complaint reports that carry no complaint date — ownership at the time is unverified; shown under "All"'} />
          <Tile label="Other-owner (ignored)" value={t.prior_owner} tone="muted" hint="Complaints dated before we acquired or after we released the number" />
        </div>
      )}

      {/* trend + breakdowns */}
      {s && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 bg-white rounded-lg shadow p-4">
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="font-semibold text-gray-800">Complaints per week</h2>
              <span className="text-xs text-gray-400">last 12 weeks · by date of the complained-about call</span>
            </div>
            <WeeklyBars weeks={s.weekly} />
          </div>
          <div className="bg-white rounded-lg shadow p-4 space-y-3 text-sm">
            <div>
              <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-1">By source</h3>
              {s.by_source.length === 0 ? <p className="text-gray-400 text-xs">none</p> : s.by_source.map((r) => (
                <div key={r.source} className="flex justify-between py-0.5"><span>{SOURCE_LABEL[r.source] ?? r.source}</span><span className="tabular-nums text-gray-700">{n(r.hits)} <span className="text-gray-400">· {n(r.dids)} nums</span></span></div>
              ))}
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-1">By type</h3>
              {s.by_type.map((r) => (
                <div key={r.complaint_type} className="flex justify-between py-0.5"><span className="truncate pr-2">{r.complaint_type}</span><span className="tabular-nums text-gray-700">{n(r.hits)}</span></div>
              ))}
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Top states</h3>
              <div className="flex flex-wrap gap-1">
                {s.by_state.map((r) => (
                  <span key={r.state} className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs tabular-nums">{r.state} {n(r.hits)}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* by owner */}
      {s && s.by_owner.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-baseline justify-between">
            <h2 className="font-semibold text-gray-800">By owner</h2>
            <span className="text-xs text-gray-400">"still active" = their numbers with complaints that are still dialing</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500"><tr>
                <th className="px-4 py-2">Owner</th><th className="px-4 py-2 text-right">Complaints</th><th className="px-4 py-2 text-right">Numbers</th>
                <th className="px-4 py-2 text-right">Quarantined</th><th className="px-4 py-2 text-right">Released</th>
                <th className="px-4 py-2 text-right">Still active</th><th className="px-4 py-2 text-right">Last 7d</th><th className="px-4 py-2">Last hit</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {s.by_owner.map((o) => (
                  <tr key={o.usr_id ?? "none"} className="cursor-pointer" onClick={() => { setOwner(o.usr_id ?? ""); setOffset(0); }}>
                    <td className="px-4 py-2 font-medium">{o.username ?? `usr ${o.usr_id}`} <span className="text-gray-400 font-normal">#{o.usr_id}</span></td>
                    <td className="px-4 py-2 text-right tabular-nums">{n(o.hits)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{n(o.dids)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{n(o.quarantined)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{n(o.released)}</td>
                    <td className={`px-4 py-2 text-right tabular-nums font-semibold ${o.still_active > 0 ? "text-red-700" : "text-gray-400"}`}>{n(o.still_active)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{n(o.hits_7d)}</td>
                    <td className="px-4 py-2 text-gray-600">{d(o.last_hit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* repeat numbers */}
      {s && s.top_dids.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100"><h2 className="font-semibold text-gray-800">Most-complained numbers</h2></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500"><tr>
                <th className="px-4 py-2">Number</th><th className="px-4 py-2">Owner</th><th className="px-4 py-2 text-right">Complaints</th>
                <th className="px-4 py-2">Sources</th><th className="px-4 py-2">First → last</th><th className="px-4 py-2">Acquired</th><th className="px-4 py-2">First used</th><th className="px-4 py-2">Status</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {s.top_dids.map((r) => (
                  <tr key={r.phone}>
                    <td className="px-4 py-2 font-mono">{r.phone}</td>
                    <td className="px-4 py-2">{r.username ?? `usr ${r.owner_usr_id}`}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">{n(r.hits)}</td>
                    <td className="px-4 py-2 text-gray-600 uppercase text-xs">{r.sources}</td>
                    <td className="px-4 py-2 text-gray-600 tabular-nums">{d(r.first_hit)} → {d(r.last_hit)}</td>
                    <td className="px-4 py-2 text-gray-600 tabular-nums">{d(r.acquired_at)}</td>
                    <td className="px-4 py-2 text-gray-600 tabular-nums">{d(r.first_used_at)}</td>
                    <td className="px-4 py-2"><StatusBadge status={r.did_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* every hit */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">All complaints <span className="text-gray-400 font-normal">({n(total)})</span></h2>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <button disabled={page <= 1} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40">‹</button>
            <span>page {page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setOffset(offset + PAGE_SIZE)} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40">›</button>
          </div>
        </div>
        {hits.isLoading ? (
          <p className="p-4 text-sm text-gray-500">Loading…</p>
        ) : hits.isError ? (
          <p className="p-4 text-sm text-red-700">{readApiError(hits.error)}</p>
        ) : (hits.data?.complaints.length ?? 0) === 0 ? (
          <p className="p-4 text-sm text-gray-500">No verified in-ownership complaints match. {oursOnly ? "Switch to \"All\" to include undated reports and other-owner history." : ""}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500"><tr>
                <th className="px-4 py-2">Call date</th><th className="px-4 py-2">Number</th><th className="px-4 py-2">Owner</th>
                <th className="px-4 py-2">Source</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">State</th>
                <th className="px-4 py-2">Acquired</th><th className="px-4 py-2">First used</th><th className="px-4 py-2">Ownership</th>
                <th className="px-4 py-2">Sweep action</th><th className="px-4 py-2">Number now</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {hits.data!.complaints.map((h) => (
                  <tr key={h.complaint_id} className={h.did_status === "active" && h.during_ownership ? "bg-red-50/40" : ""}>
                    <td className="px-4 py-2 tabular-nums">{d(h.issue_date)}</td>
                    <td className="px-4 py-2 font-mono">{h.phone}</td>
                    <td className="px-4 py-2">{h.username ?? (h.owner_usr_id != null ? `usr ${h.owner_usr_id}` : "—")}</td>
                    <td className="px-4 py-2 text-xs uppercase text-gray-600">{SOURCE_LABEL[h.source] ?? h.source}</td>
                    <td className="px-4 py-2 text-gray-700 max-w-[14rem] truncate" title={h.issue ?? undefined}>{h.complaint_type || h.issue || "—"}</td>
                    <td className="px-4 py-2 text-gray-600">{h.state || "—"}</td>
                    <td className="px-4 py-2 tabular-nums text-gray-600">{d(h.acquired_at)}</td>
                    <td className="px-4 py-2 tabular-nums text-gray-600">{d(h.first_used_at)}</td>
                    <td className="px-4 py-2"><OwnershipBadge hit={h} /></td>
                    <td className="px-4 py-2"><ActionBadge action={h.action} /></td>
                    <td className="px-4 py-2"><StatusBadge status={h.did_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
